import { ConnectionSelect } from '@app/modules/social-export/components/ConnectionSelect';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useAutoSelectConnectionId } from '@app/modules/social-export/hooks/useAutoSelectConnectionId';
import { useStationPlatformConnections } from '@app/modules/social-export/hooks/useStationPlatformConnections';
import { connectionsManageMessages as messages } from '@app/modules/social-export/screens/manage/connections-manage-messages';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

type Props = {
  stationId: string;
  platform: SocialPlatform;
  fieldPrefix: string;
};

/**
 * Ensures a connectionId is set for quick export when the platform toggle is on.
 * Shows a Select only when the station has multiple connections for the platform.
 */
export function QuickPlatformConnectionField({ stationId, platform, fieldPrefix }: Props) {
  const { $t } = useIntl();
  const { setValue, watch } = useFormContext();
  const { connections } = useStationPlatformConnections(stationId, platform);
  const connectionId = (watch(`${fieldPrefix}.connectionId`) as string | undefined) ?? null;

  const handleChange = useCallback(
    (id: string | null) => {
      setValue(`${fieldPrefix}.connectionId`, id ?? '');
    },
    [fieldPrefix, setValue],
  );

  useAutoSelectConnectionId(connections, connectionId, handleChange);

  if (connections.length <= 1) {
    return null;
  }

  return (
    <ConnectionSelect
      id={`${fieldPrefix}-connection-select`}
      connections={connections}
      value={connectionId}
      onChange={handleChange}
      platform={platform}
      size="small"
      label={$t(messages.selectConnectionForPublish)}
      sx={{ mt: 1, maxWidth: 320 }}
    />
  );
}
